import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { companyApi, customerApi, inspectionApi } from '../api/endpoints.ts';
import { Alert, Button, classes, EmptyState, LoadingBlock, Pagination, Select, TextInput } from '../components/ui.tsx';
import { WorkPage, WorkSection } from '../components/work-page.tsx';
import SearchSelect from '../components/search-select.tsx';
import {
  chooseLineSource,
  InspectionLineSourceField,
  useInspectionLineSources,
  type InspectionLineSource,
} from '../components/inspection-line-source.tsx';
import { todayInTimezone } from '../components/schedule/format.ts';
import { useSession } from '../state/session.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import { formatShortDate } from '../i18n/datetime.ts';
import { INSPECTION_STATUSES, type InspectionStatus } from '@shared/constants/index.ts';
import type { Inspection, OpenInspectionAction, Property } from '@shared/types/index.ts';

/**
 * Formats a stored score for reading.
 *
 * Basis points of the achievable total, so an eleven-area walkthrough and a
 * forty-area one are comparable. Null is not 0% — it is a walkthrough where
 * nothing could be scored, and printing a zero for it would libel the crew.
 */
export function formatScore(basisPoints: number | null): string | null {
  return basisPoints === null ? null : `${Math.round(basisPoints / 100)}%`;
}

/**
 * Today, in the company's calendar rather than the browser's.
 *
 * Inspection dates are company-local, so a supervisor in Denver opening this
 * page at 6pm must not see work due today marked overdue because UTC has
 * already rolled over.
 */
function useCompanyToday(): string {
  const { company, currentLocation } = useSession();
  return todayInTimezone(currentLocation?.timezone ?? company?.timezone);
}

/**
 * Quality inspections: the scored walkthroughs, and what is still outstanding
 * from them.
 *
 * The open actions sit at the top rather than on each inspection, because the
 * question that loses a contract is asked across walkthroughs — "what did we
 * promise to fix and never fix?" — and an answer filed under the inspection
 * that raised it is an answer nobody goes looking for.
 */
export default function InspectionsPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('inspections.title'));
  const queryClient = useQueryClient();
  const { can } = useSession();
  const [status, setStatus] = useState<'' | InspectionStatus>('');
  const [page, setPage] = useState(1);
  const [scheduling, setScheduling] = useState(false);

  // A read-only member reaches this page by design — the nav is gated on
  // `schedule:read`. What they must not get is a form whose every button the
  // server would refuse.
  const canWrite = can('schedule:write');

  const inspections = useQuery({
    queryKey: ['inspections', status, page],
    queryFn: () => inspectionApi.list({ ...(status ? { status } : {}), page }),
  });

  const totalPages = inspections.data
    ? Math.max(1, Math.ceil(inspections.data.total / inspections.data.pageSize))
    : undefined;

  return (
    <WorkPage
      title={t('inspections.title')}
      description={t('inspections.description')}
      actions={
        canWrite ? (
          <Button variant="primary" onClick={() => setScheduling((open) => !open)}>
            {scheduling ? t('common.cancel') : t('inspections.schedule')}
          </Button>
        ) : null
      }
    >
      {scheduling && canWrite ? (
        <InspectionComposer
          onCreated={() => {
            setScheduling(false);
            void queryClient.invalidateQueries({ queryKey: ['inspections'] });
          }}
        />
      ) : null}

      <OpenActions canWrite={canWrite} />

      <WorkSection title={t('inspections.backToList')}>
        <div className="mb-4 w-full sm:max-w-sm">
          <Select
            label={t('inspections.filterStatus')}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as '' | InspectionStatus);
              // A narrower list has fewer pages; staying on page 4 of the old
              // one would land on an empty screen.
              setPage(1);
            }}
            options={[
              { value: '', label: t('inspections.status.all') },
              ...INSPECTION_STATUSES.map((value) => ({ value, label: t(`inspections.status.${value}`) })),
            ]}
          />
        </div>

        {inspections.isLoading ? <LoadingBlock label={t('inspections.loading')} /> : null}
        {inspections.isError ? (
          <Alert tone="error" title={t('inspections.loadErrorTitle')}>
            {(inspections.error as Error).message}
          </Alert>
        ) : null}

        {inspections.data && inspections.data.items.length === 0 ? (
          <EmptyState title={t('inspections.emptyTitle')}>
            <p>{t('inspections.emptyBody')}</p>
          </EmptyState>
        ) : null}

        {inspections.data && inspections.data.items.length > 0 ? (
          <ul className="divide-y divide-[color:var(--color-line)]">
            {inspections.data.items.map((inspection) => (
              <InspectionRow key={inspection.id} inspection={inspection} />
            ))}
          </ul>
        ) : null}

        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </WorkSection>
    </WorkPage>
  );
}

function InspectionRow({ inspection }: { inspection: Inspection }) {
  const { t } = useTranslation();
  const score = formatScore(inspection.scoreBasisPoints);

  return (
    <li className="flex flex-wrap items-start justify-between gap-3 py-3">
      <div className="min-w-0 flex-1 sm:min-w-56">
        <p className="font-semibold">
          <Link to={`/inspections/${inspection.id}`} className="text-[color:var(--color-brand)] hover:underline">
            {inspection.siteName ?? t('inspections.unnamedSite')}
          </Link>
          <span className="ml-2 badge border-[color:var(--color-line)] text-[color:var(--color-muted)]">
            {t(`inspections.status.${inspection.status}`)}
          </span>
          {/*
            Shown only when sharing is on; "internal", which is every other
            row, is not worth a badge on all of them. Two wordings, because
            sharing can be switched on before the walkthrough is finished and
            the portal only ever publishes COMPLETED ones — a row that says
            the client can see a scorecard nobody has finished would be a
            claim the portal does not honor.
          */}
          {inspection.sharedWithClient ? (
            <span className="ml-2 badge border-[color:var(--color-brand)] text-[color:var(--color-brand)]">
              {inspection.status === 'completed' ? t('inspections.sharedBadge') : t('inspections.sharedPendingBadge')}
            </span>
          ) : null}
        </p>
        <p className="text-sm text-[color:var(--color-muted)]">
          {inspection.customerName ?? t('inspections.unnamedCustomer')} ·{' '}
          {formatShortDate(inspection.completedAt?.slice(0, 10) ?? inspection.scheduledFor)}
        </p>
      </div>
      {/*
        The number, or an explicit "nothing scored". A blank cell where a
        percentage goes reads as a page that failed to load.
      */}
      <p className="w-28 text-right font-semibold">
        {score ?? <span className="font-normal text-[color:var(--color-muted)]">{t('inspections.noScore')}</span>}
      </p>
    </li>
  );
}

/**
 * Everything still outstanding, across every walkthrough.
 *
 * Deliberately above the list of inspections: "we scored it a 2 and did
 * nothing" is the pattern a client eventually leaves over, and it is only
 * visible when the actions are gathered away from the walkthroughs that
 * raised them.
 */
function OpenActions({ canWrite }: { canWrite: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const today = useCompanyToday();
  const actions = useQuery({ queryKey: ['inspection-actions', 'open'], queryFn: () => inspectionApi.openActions() });
  const [resolving, setResolving] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const resolve = useMutation({
    mutationFn: (actionId: string) => inspectionApi.resolveAction(actionId, { resolutionNote: note || null }),
    onSuccess: () => {
      setResolving(null);
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['inspection-actions'] });
      // The walkthrough's own copy of this action is now stale too.
      void queryClient.invalidateQueries({ queryKey: ['inspection'] });
    },
  });

  /**
   * Opens the note field against one action, closing whichever was open.
   *
   * The note is cleared on every switch: one shared field that kept its text
   * would file the explanation typed for one action against the next one
   * somebody opened, which is a wrong record rather than a wrong keystroke.
   */
  function openFor(actionId: string) {
    setResolving((current) => (current === actionId ? null : actionId));
    setNote('');
  }

  /*
    A failure must not read as "nothing outstanding". This panel's whole job
    is to surface work somebody promised to do, so a fetch that fell over is
    reported where the list would have been — the one state where silence is
    indistinguishable from good news.
  */
  if (actions.isError) {
    return (
      <section className="card mt-6">
        <Alert tone="error" title={t('inspections.openActionsErrorTitle')}>
          {(actions.error as Error).message}
        </Alert>
      </section>
    );
  }

  // Nothing outstanding is good news and needs no panel: an empty state here
  // would compete with the list of walkthroughs for the top of the page.
  if (!actions.data || actions.data.items.length === 0) return null;

  const hidden = actions.data.total - actions.data.items.length;

  return (
    <section className="card mt-6">
      <h2 className="mb-1 text-lg font-semibold">{t('inspections.openActionsTitle')}</h2>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('inspections.openActionsBody')}</p>

      {/*
        Said out loud when the list is capped. A truncated work list that
        cannot say so is read as the whole of what is outstanding.
      */}
      {hidden > 0 ? (
        <Alert tone="warning" title={t('inspections.openActionsTruncatedTitle')}>
          {t('inspections.openActionsTruncatedBody', { shown: actions.data.items.length, total: actions.data.total })}
        </Alert>
      ) : null}

      <ul className="divide-y divide-[color:var(--color-line)]">
        {actions.data.items.map((action) => (
          <li key={action.id} className="py-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1 sm:min-w-56">
                <p>{action.description}</p>
                <p className="text-sm text-[color:var(--color-muted)]">
                  <Link
                    to={`/inspections/${action.inspectionId}`}
                    className="text-[color:var(--color-brand)] hover:underline"
                  >
                    {action.siteName ?? t('inspections.unnamedSite')}
                  </Link>{' '}
                  · {action.customerName ?? t('inspections.unnamedCustomer')} · {formatShortDate(action.scheduledFor)}
                </p>
              </div>
              <DueDate dueOn={action.dueOn} today={today} />
              {canWrite ? <Button onClick={() => openFor(action.id)}>{t('inspections.resolveAction')}</Button> : null}
            </div>

            {resolving === action.id ? (
              <div className="mt-2 flex flex-wrap items-end gap-2">
                <div className="min-w-0 flex-1 sm:min-w-64">
                  <TextInput
                    label={t('inspections.resolutionNote')}
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                  />
                </div>
                <div className="mb-4">
                  <Button variant="primary" busy={resolve.isPending} onClick={() => resolve.mutate(action.id)}>
                    {t('inspections.confirmResolve')}
                  </Button>
                </div>
              </div>
            ) : null}
          </li>
        ))}
      </ul>

      {resolve.isError ? (
        <Alert tone="error" title={t('inspections.resolveErrorTitle')}>
          {(resolve.error as Error).message}
        </Alert>
      ) : null}
    </section>
  );
}

/** The due date, marked when it has passed — which is the whole point of the list. */
function DueDate({ dueOn, today }: { dueOn: string | null; today: string }) {
  const { t } = useTranslation();
  if (!dueOn) return <p className="w-32 text-sm text-[color:var(--color-muted)]">{t('inspections.noDueDate')}</p>;
  const overdue = dueOn < today;
  return (
    // The word carries it; the color only makes it noticeable.
    <p className={classes('w-32 text-sm', overdue && 'font-semibold text-[color:var(--color-danger)]')}>
      {overdue ? t('inspections.overdueOn', { date: formatShortDate(dueOn) }) : formatShortDate(dueOn)}
    </p>
  );
}

/**
 * Scheduling one: a site, a date, what will be checked, and optionally who is
 * walking it.
 *
 * The site rather than the customer is what an inspection is against — a
 * facilities manager with four buildings has four different scorecards — so
 * the customer picker here exists only to narrow down to the site.
 *
 * The list of checks is settled here rather than on the walk, so every
 * walkthrough of a building is scored against the same standard and two of
 * them can be compared.
 */
function InspectionComposer({ onCreated }: { onCreated: (inspection: Inspection) => void }) {
  const { t } = useTranslation();
  const today = useCompanyToday();
  const [customerId, setCustomerId] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [scheduledFor, setScheduledFor] = useState(today);
  const [inspectorUserId, setInspectorUserId] = useState('');
  // Null follows the suggestion — the building's checklist when it has one —
  // until somebody picks a list themselves.
  const [lineSource, setLineSource] = useState<InspectionLineSource | null>(null);
  const lineSources = useInspectionLineSources(propertyId, today);
  const lines = chooseLineSource(lineSources, lineSource);

  /*
    Searchable rather than a flat first page. A company with more than a
    hundred clients could otherwise not schedule a walkthrough for any client
    whose name fell off the end of page one — the picker would look complete
    and simply not contain them.

    The search box and the picker are one control (SearchSelect), which does
    the debouncing: this holds only what the server was last asked about.
  */
  const [customerQuery, setCustomerQuery] = useState('');

  const customers = useQuery({
    queryKey: ['customers', 'inspection-picker', customerQuery],
    queryFn: () => customerApi.list({ pageSize: 100, ...(customerQuery ? { q: customerQuery } : {}) }),
  });

  const properties = useQuery({
    queryKey: ['customer-properties', customerId],
    queryFn: () => customerApi.properties(customerId),
    enabled: Boolean(customerId),
  });

  const team = useQuery({ queryKey: ['company-users'], queryFn: () => companyApi.users() });

  const create = useMutation({
    mutationFn: () =>
      inspectionApi.create({
        propertyId,
        scheduledFor,
        inspectorUserId: inspectorUserId || null,
        items: lines.lines,
      }),
    onSuccess: onCreated,
  });

  const siteLabel = (property: Property) => property.name ?? property.addressLine1 ?? t('inspections.unnamedSite');

  // Deactivated members are not offered, the same rule the schedule's
  // assignee picker keeps: sending somebody who can no longer sign in to walk
  // a building produces a walkthrough nobody is going to do.
  const inspectors = (team.data?.items ?? []).filter((member) => member.isActive);

  return (
    <section className="card mt-6">
      <h2 className="mb-4 text-lg font-semibold">{t('inspections.schedule')}</h2>
      <div className="grid gap-x-4 sm:grid-cols-2">
        <SearchSelect
          label={t('inspections.customer')}
          hint={t('searchSelect.hint')}
          placeholder={t('inspections.chooseCustomer')}
          value={customerId}
          onChange={(next) => {
            setCustomerId(next);
            // The site belonged to the previous client. Keeping it would let a
            // walkthrough be filed against a building nobody chose.
            setPropertyId('');
            setLineSource(null);
          }}
          onSearch={setCustomerQuery}
          loading={customers.isFetching}
          errored={customers.isError}
          options={(customers.data?.items ?? []).map((customer) => ({
            value: customer.id,
            label: customer.displayName,
          }))}
        />
        <Select
          label={t('inspections.site')}
          value={propertyId}
          disabled={!customerId || properties.isLoading}
          onChange={(event) => {
            setPropertyId(event.target.value);
            // A list picked for one building says nothing about the next.
            setLineSource(null);
          }}
          options={[
            { value: '', label: t('inspections.chooseSite') },
            ...(properties.data?.items ?? []).map((property) => ({
              value: property.id,
              label: siteLabel(property),
            })),
          ]}
        />
        <TextInput
          label={t('inspections.scheduledFor')}
          type="date"
          value={scheduledFor}
          onChange={(event) => setScheduledFor(event.target.value)}
        />
        <Select
          label={t('inspections.inspector')}
          value={inspectorUserId}
          onChange={(event) => setInspectorUserId(event.target.value)}
          options={[
            { value: '', label: t('inspections.inspectorUnassigned') },
            ...inspectors.map((member) => ({ value: member.id, label: member.fullName || member.email })),
          ]}
        />
      </div>

      {propertyId ? <InspectionLineSourceField sources={lineSources} chosen={lines} onChange={setLineSource} /> : null}

      {/*
        A picker that failed to load renders as "Choose a client" with nothing
        under it — indistinguishable from a search that matched nobody, and it
        makes scheduling look impossible rather than broken. All three of these
        are required to schedule anything, so all three say when they failed.
      */}
      {customers.isError || properties.isError || team.isError ? (
        <Alert tone="error" title={t('inspections.pickerErrorTitle')}>
          {((customers.error ?? properties.error ?? team.error) as Error).message}
        </Alert>
      ) : null}

      {customers.data && customers.data.total > customers.data.items.length ? (
        <p className="field-hint">
          {t('inspections.customerSearchTruncated', {
            shown: customers.data.items.length,
            total: customers.data.total,
          })}
        </p>
      ) : null}

      {customerId && properties.data && properties.data.items.length === 0 ? (
        <Alert tone="warning" title={t('inspections.noSitesTitle')}>
          {t('inspections.noSitesBody')}
        </Alert>
      ) : null}

      {create.isError ? (
        <Alert tone="error" title={t('inspections.createErrorTitle')}>
          {(create.error as Error).message}
        </Alert>
      ) : null}

      <div className="mt-4">
        <Button
          variant="primary"
          busy={create.isPending}
          // Not before the building's own checklist has had the chance to
          // load: scheduling in that moment would file the walkthrough against
          // the generic list without anybody having chosen it.
          disabled={!propertyId || !scheduledFor || (lineSources.loading && lineSource === null)}
          onClick={() => create.mutate()}
        >
          {t('inspections.saveSchedule')}
        </Button>
      </div>
    </section>
  );
}
